import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ProjectLinkItem } from '@/components/ProjectLinkItem';
import { ProjectLinkPasswordDialog } from '@/components/ProjectLinkPasswordDialog';
import { execute } from '@/graphql/execute';
import { deleteProjectLinkMutation } from '@/mutations/project.mutation';
import { useDialog } from '@/providers/DialogProvider';
import { getAllProjectLinksQuery } from '@/queries/project.query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export function OrganizationShareLinks() {
  const { t } = useTranslation('orgSettings');
  const { t: tComponents } = useTranslation('components');
  const { openDialog } = useDialog();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['allProjectLinks'],
    queryFn: () => execute(getAllProjectLinksQuery),
  });

  const deleteLinkMutation = useMutation({
    mutationFn: async (linkId: string) => {
      return execute(deleteProjectLinkMutation, { id: linkId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allProjectLinks'] });
      toast.success(tComponents('shareDialog.linkDeleted'));
    },
    onError: (error: any) => {
      toast.error(error.message || tComponents('shareDialog.failedDeleteLink'));
    },
  });

  const _handleDeleteLink = async (linkId: string) => {
    if (confirm(tComponents('shareDialog.confirmDeleteLink'))) {
      await deleteLinkMutation.mutateAsync(linkId);
    }
  };

  const _handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(tComponents('shareDialog.linkCopied'));
    } catch (error) {
      toast.error(tComponents('shareDialog.failedCopyLink'));
    }
  };

  const _handleEditPassword = (link: any) => {
    openDialog(
      <ProjectLinkPasswordDialog
        linkId={link.id}
        linkName={link.name || tComponents('shareDialog.untitled')}
        hasPassword={link.hasPassword}
        projectId={link.projectId}
      />,
    );
  };

  const isLinkExpired = (expiresAt: string) => {
    return dayjs(expiresAt).isBefore(dayjs());
  };

  const links = data?.allProjectLinks || [];
  const activeLinks = links.filter((link) => link.status === 'ACTIVE');
  const expiredLinks = links.filter((link) => link.status === 'EXPIRED');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('shareLinks.title')}</CardTitle>
        <CardDescription>{t('shareLinks.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='text-sm text-muted-foreground'>{t('shareLinks.loading')}</div>
        ) : links.length === 0 ? (
          <div className='text-sm text-muted-foreground'>{t('shareLinks.noLinks')}</div>
        ) : (
          <div className='flex flex-col gap-2'>
            {activeLinks.map((link) => (
              <ProjectLinkItem
                key={link.id}
                link={link}
                projectId={link.projectId}
                projectName={link.project?.name || t('shareLinks.unknownProject')}
                onCopy={_handleCopyLink}
                onDelete={_handleDeleteLink}
                onEditPassword={_handleEditPassword}
                isLinkExpired={isLinkExpired}
                deletePending={deleteLinkMutation.isPending}
              />
            ))}
            {!!expiredLinks.length && (
              <div className='text-sm font-medium text-slate-500 dark:text-stone-200/70 mt-4 flex items-center justify-between'>
                <span>{tComponents('shareDialog.expiredLinks')}</span>
              </div>
            )}
            {expiredLinks.map((link) => (
              <ProjectLinkItem
                key={link.id}
                link={link}
                projectId={link.projectId}
                projectName={link.project?.name || t('shareLinks.unknownProject')}
                onCopy={_handleCopyLink}
                onDelete={_handleDeleteLink}
                onEditPassword={_handleEditPassword}
                isLinkExpired={isLinkExpired}
                deletePending={deleteLinkMutation.isPending}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
